import React from 'react';
import { Grid, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { IrpItemField } from '../context/irp-validation';
import { IIrpItemAddEditRequest } from '../../../../model/scp/irp/request/irp-item-add-edit-request';
import { ITableResponse } from '../../../../model/scp/purchase/response/tables-response';

const DESCRIPTION_MAX_LENGTH = 2048;

export interface IrpItemFormProps {
    itemIrp: IIrpItemAddEditRequest;
    materialService: ITableResponse[];
    categoryItemPca: ITableResponse[];
    judgementCriteria: ITableResponse[];
    isRetification: boolean;
    errors: ValidationErrors<IrpItemField>;
    onChange: (e: any) => void;
    onUnitValueChange: (value: number) => void;
}

export default function IrpItemForm({ itemIrp, materialService, categoryItemPca, judgementCriteria, isRetification, errors, onChange, onUnitValueChange }: IrpItemFormProps) {
    const descriptionLength = itemIrp.description?.length ?? 0;

    return (
        <React.Fragment>
            {isRetification && (
                <Typography variant="body2" color="text.secondary">
                    Este item já foi enviado ao PNCP. Apenas descrição, informação complementar, unidade de fornecimento
                    e valor unitário estimado podem ser retificados, mediante justificativa.
                </Typography>
            )}

            <FormDrawerGroup title="Classificação do item">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required={!isRetification} disabled={isRetification} label="Tipo" name="select-type-item" value={itemIrp.typeItem} options={toLookupOptions(materialService)} onChange={onChange} error={errors.typeItem} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required={!isRetification} disabled={isRetification} label="Categoria" name="select-category-item" value={itemIrp.categoryItemId} options={toLookupOptions(categoryItemPca)} onChange={onChange} error={errors.categoryItemId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required={!isRetification} disabled={isRetification} label="Critério de Julgamento" name="select-criterion-judgment" value={itemIrp.criterionJudgmentId} options={toLookupOptions(judgementCriteria)} onChange={onChange} error={errors.criterionJudgmentId} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Valores e medidas">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Unidade de Fornecimento</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="input-unit-supply" inputProps={{ maxLength: 30 }} value={itemIrp.unitSupply || ''} onChange={onChange} error={!!errors.unitSupply} />
                        <FieldError message={errors.unitSupply} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Valor Unitário Estimado</InputLabel>
                        <BrazilianNumberInput fullWidth value={itemIrp.unitValueEstimated} onChange={onUnitValueChange} decimals={4} prefix="R$" placeholder="0,0000" error={!!errors.unitValueEstimated} />
                        <FieldError message={errors.unitValueEstimated} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Descrição">
                <InputLabel required>Descrição</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" multiline rows={5} name="input-description" inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }} value={itemIrp.description || ''} onChange={onChange} error={!!errors.description} />
                <FieldError message={errors.description} />
                <Typography variant="caption" color={descriptionLength >= DESCRIPTION_MAX_LENGTH ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                    {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
                </Typography>
                {isRetification && (
                    <React.Fragment>
                        <InputLabel sx={{ mt: 2 }}>Informação Complementar</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-complementary-information-item" value={itemIrp.complementaryInformation || ''} onChange={onChange} />
                    </React.Fragment>
                )}
            </FormDrawerGroup>

            {isRetification ? (
                <FormDrawerGroup title="Justificativa">
                    <InputLabel required>Justificativa da Retificação</InputLabel>
                    <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-justification-item" value={itemIrp.justification || ''} onChange={onChange} error={!!errors.justification} />
                    <FieldError message={errors.justification} />
                </FormDrawerGroup>
            ) : (
                <FormDrawerGroup title="Opções adicionais">
                    <Grid container spacing={1.5} direction="column">
                        <Grid item>
                            <SwitchField label="Aplicabilidade de Margem de Preferência" checked={itemIrp.applicabilityMarginPreference || false} onChange={(checked) => onChange({ target: { name: 'check-applicability-margin-preference', checked } })} />
                        </Grid>
                        <Grid item>
                            <SwitchField label="Exigência de Conteúdo Nacional" checked={itemIrp.requirementNationalContent || false} onChange={(checked) => onChange({ target: { name: 'check-requirement-national-content', checked } })} />
                        </Grid>
                        <Grid item>
                            <SwitchField label="Valor Sigiloso" checked={itemIrp.indConfidentialValue || false} onChange={(checked) => onChange({ target: { name: 'check-ind-confidential-value', checked } })} />
                        </Grid>
                    </Grid>
                </FormDrawerGroup>
            )}
        </React.Fragment>
    );
}
